<template>
  <div
    class="input-tooltip"
    :class="{ [$class('hidden')]: hidden }"
  >
    <button
      ref="button"
      class="input-tooltip__button"
      type="button"
      :tabindex="hidden ? -1 : null"
      :aria-expanded="tooltipActive"
      aria-haspopup="dialog"
      @click="handleTooltipToggle"
    >
      <transition name="fade">
        <component :is="buttonIcon" />
      </transition>

      <span
        class="visually-hidden"
        v-text="buttonText"
      />
    </button>

    <teleport to="body">
      <div
        ref="tooltip"
        class="input-tooltip__tooltip text-body-s-regular"
        :class="{ [$class('active')]: tooltipActive }"
        role="dialog"
        :style="tooltipStyles"
        v-html="tooltipText"
      />
    </teleport>
  </div>
</template>

<script>

/**
 * Vue: Input tooltip (vehicle-finder-form)
 * -----------------------------------------------------------------------------
 * Tooltip for input.
 *
 * @param {Boolean} [hidden] - Hidden.
 * @param {String} text - Tooltip text.
 * @param {Boolean} tooltipActive - Tooltip active state.
 *
 * @emits updateTooltip - Emit tooltip state update.
 *
 */
import IconCloseFill from '~icons/directional-navigation/close-fill.svg'
import IconInfo from '~icons/general/info.svg'

export default {
  name: 'InputTooltip',

  components: {
    IconCloseFill,
    IconInfo,
  },

  props: {
    hidden: {
      type: Boolean,
      default: false,
    },
    text: {
      type: String,
      default: '',
      required: true,
    },
    tooltipActive: {
      type: Boolean,
      default: false,
      required: true,
    },
  },

  emits: {
    'update-tooltip': (payload) => typeof payload === 'boolean',
  },

  data() {
    return {
      tooltipStyles: {},
    }
  },

  computed: {

    /**
     * Compute button icon.
     * @returns {String}
     */
    buttonIcon() {
      return this.tooltipActive
        ? 'icon-close-fill'
        : 'icon-info'
    },

    /**
     * Compute button text.
     * @returns {String}
     */
    buttonText() {
      return this.tooltipActive
        ? this.$string('accessibility.close')
        : this.$string('vehicle_finder.tooltip.open')
    },

    /**
     * Compute tooltip text HTML.
     * @returns {String}
     */
    tooltipText() {
      return this.text
        ?.replaceAll('<strong', '<strong class="text-heading-m"')
        ?.replaceAll('<p', '<p class="text-body-s-regular"')
        ?.replaceAll('<a', '<span><a class="text-body-s-underline"')
        ?.replaceAll('</a>', '</a></span>')
    },
  },

  watch: {

    /**
     * Watch tooltip active to set tooltip element position.
     */
    tooltipActive() {
      this.setPosition()
    },
  },

  mounted() {
    cnvs.EventBus.listen('cnvs:esc', this.handleTooltipClose)
    cnvs.EventBus.listen('cnvs:resize', this.setPosition)

    document.addEventListener('click', this.handleClickOutside)
  },

  unmounted() {
    document.removeEventListener('click', this.handleClickOutside)
  },

  methods: {

    /**
     * Set position of element.
     */
    setPosition() {
      if (!this.tooltipActive) {
        return
      }

      const screenPadding = 16
      const width = 300
      const iconSize = this.$refs.button.offsetWidth

      const buttonRect = this.$refs.button.getBoundingClientRect()
      this.$refs.tooltip.scroll(0, 0)

      let direction = 'left'
      let xPosition = `${buttonRect.x}px`

      /**
       * If tooltip exceeds viewport then adjust position.
       */
      if ((buttonRect.x + width + screenPadding) > window.innerWidth) {
        direction = 'right'
        xPosition = `${window.innerWidth - buttonRect.x - iconSize}px`
      }

      this.tooltipStyles = {
        [direction]: xPosition,
        top: `${buttonRect.y + iconSize + window.scrollY}px`,
      }
    },

    /**
     * Handle tooltip toggle.
     */
    handleTooltipToggle() {
      this.$emit('update-tooltip', !this.tooltipActive)
    },

    /**
     * Handle click outside.
     */
    handleTooltipClose() {
      if (!this.tooltipActive) {
        return
      }

      this.$emit('update-tooltip', false)
    },

    /**
     * Handle click outside.
     * @param {Object} event - Click event.
     */
    handleClickOutside(event) {
      const elements = [
        this.$refs.tooltip,
        this.$refs.button,
        this.$refs.tooltip,
      ]

      if (!this.tooltipActive || elements.includes(event.target)) {
        return
      }

      this.handleTooltipClose()
    },
  },
}
</script>

<style lang="scss">
@import './input-tooltip';
</style>
